import React from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

// Import styles
import { flex, media } from 'components/styles';

// Import components
import { Heading } from 'components/elements';
import LoginForm from './login_form/LoginForm';
import LoginError from '../login/LoginError';
import FirebaseAuthentication from 'components/views/authentication/login/firebase_auth/FirebaseAuthentication';
import ProviderLoginModal from 'components/views/authentication/login/firebase_auth/ProviderLoginModal/ProviderLoginModal';

// Import helpers
import { getCustomerAreaData } from 'helpers/operators';
import { useLoginSettings, formatProviderTypeString } from 'components/views/authentication/login/login_content/helpers';

// Import variables
import { LOGIN_SYSTEM_TYPES } from 'helpers/variables';

const LoginContent = () => {
	const { provider, isError } = useSelector(({ provider }) => provider);
	const { t } = useTranslation();

	const { caText, caUrl, caLogoAlt, caLogoUrl } = getCustomerAreaData();
	const providerType = provider && formatProviderTypeString(provider);

	const { loginInfo, renderFirebaseAuth } = useLoginSettings(LOGIN_SYSTEM_TYPES.vectra, provider, isError);

	return (
		<LoginContentStyled>
			<HeadingStyled data-testid="login-heading">{t('common_login_login_form_title')}</HeadingStyled>
			<Info modifiers="small">
				{caText}
				<Anchor href={caUrl} rel="noopener noreferrer" target="_blank">
					<LoginContentImg src={caLogoUrl} alt={caLogoAlt} title={caLogoAlt} />
				</Anchor>
			</Info>
			{isError && <Info>{loginInfo}</Info>}
			<LoginError />
			<LoginForm />
			{renderFirebaseAuth && <FirebaseAuthentication />}
			{isError && <ProviderLoginModal providerType={providerType} />}
		</LoginContentStyled>
	);
};

const baseHeading = css`
	color: ${({ theme }) => theme.white};
	margin-bottom: 1.8rem;
	line-height: 1.4;
	font-weight: ${({ theme }) => theme.font.medium};
`;

const LoginContentStyled = styled.div`
	background-color: ${({ theme }) => theme.white};
	border-radius: 0.4rem;
	width: 100%;
	padding: 1.8rem;
	text-align: center;
	${media.min.xmin(css`
		max-width: 60rem;
		margin: 0 auto;
	`)}
	${media.min.large(css`
		margin: 0;
		max-width: unset;
	`)}
`;

const HeadingStyled = styled(Heading.First)`
	color: ${({ theme }) => theme.gray};
`;

const Anchor = styled.a`
	display: inline-block;
	margin-left: 0.5rem;
`;

const Info = styled.p`
	${baseHeading}
	color: ${({ theme }) => theme.palette.primary.main};
	${flex()}
	font-size: 1.4rem;
`;

const LoginContentImg = styled.img`
	height: auto;
	width: 16rem;
	object-position: left center;
	object-fit: contain;
`;

export default LoginContent;
